/**
 * Original, simplified NZ left-side road-layout underlays for the Driving Sketch Pad.
 * Educational schematic aids only — not official NZTA/Road Code artwork copies.
 * Drawn on an 800x500 canvas coordinate space to match DrivingSketchPad.
 */
export const SKETCH_TEMPLATE_IDS = [
  "blank",
  "blank-road",
  "t-intersection",
  "four-way",
  "roundabout",
  "multi-lane-roundabout",
  "flush-median",
  "parallel-parking",
  "lane-change",
  "three-point",
  "u-turn",
  "railway",
  "open-junction",
  "closed-junction",
  "divergent-movement",
  "meeting-point",
];

export const SKETCH_TEMPLATE_LABELS = {
  blank: "Blank",
  "blank-road": "Straight road",
  "t-intersection": "T-intersection",
  "four-way": "Four-way",
  roundabout: "Roundabout",
  "multi-lane-roundabout": "Multi-lane roundabout",
  "flush-median": "Flush median",
  "parallel-parking": "Parallel parking",
  "lane-change": "Lane change",
  "three-point": "Three-point turn",
  "u-turn": "U-turn",
  railway: "Railway crossing",
  "open-junction": "Open junction",
  "closed-junction": "Closed junction",
  "divergent-movement": "Divergent movement",
  "meeting-point": "Meeting point",
};

const ROAD = "#d7dde5";
const EDGE = "#64748b";
const DASH = "#94a3b8";
const HATCH = "#b7c0cc";
const KERB = "#475569";

function VerticalRoad({ x = 320, width = 160 }) {
  return (
    <g>
      <rect x={x} y={0} width={width} height={500} fill={ROAD} />
      <line x1={x} y1={0} x2={x} y2={500} stroke={EDGE} strokeWidth="3" />
      <line x1={x + width} y1={0} x2={x + width} y2={500} stroke={EDGE} strokeWidth="3" />
      <line x1={x + width / 2} y1={0} x2={x + width / 2} y2={500} stroke={DASH} strokeWidth="3" strokeDasharray="18 14" />
    </g>
  );
}

function HorizontalRoad({ y = 170, height = 160 }) {
  return (
    <g>
      <rect x={0} y={y} width={800} height={height} fill={ROAD} />
      <line x1={0} y1={y} x2={800} y2={y} stroke={EDGE} strokeWidth="3" />
      <line x1={0} y1={y + height} x2={800} y2={y + height} stroke={EDGE} strokeWidth="3" />
      <line x1={0} y1={y + height / 2} x2={800} y2={y + height / 2} stroke={DASH} strokeWidth="3" strokeDasharray="18 14" />
    </g>
  );
}

/** Fixed-angle arrowhead triangle (avoids SVG <marker> id collisions when several underlays render at once). */
function Arrowhead({ x, y, angleDeg, color = "#c62828", size = 14 }) {
  return (
    <polygon
      points={`0,-${size / 1.6} ${size},0 0,${size / 1.6}`}
      fill={color}
      transform={`translate(${x} ${y}) rotate(${angleDeg})`}
    />
  );
}

export default function SketchTemplateUnderlay({ templateId = "blank", width = 800, height = 500, className = "" }) {
  const common = {
    viewBox: "0 0 800 500",
    width: "100%",
    height: "100%",
    preserveAspectRatio: "xMidYMid slice",
    className,
    role: "img",
    "aria-label": `${SKETCH_TEMPLATE_LABELS[templateId] || "Blank"} underlay`,
  };

  switch (templateId) {
    case "blank-road":
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
          <VerticalRoad />
        </svg>
      );

    case "t-intersection":
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
          <HorizontalRoad y={170} height={160} />
          <VerticalRoad x={320} width={160} />
          {/* Minor road stops at the through road — give-way line */}
          <rect x={320} y={330} width={160} height={500 - 330} fill="#fff" />
          <line x1={330} y1="330" x2="470" y2="330" stroke="#c62828" strokeWidth="4" />
        </svg>
      );

    case "four-way":
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
          <HorizontalRoad y={170} height={160} />
          <VerticalRoad x={320} width={160} />
        </svg>
      );

    case "roundabout":
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
          <HorizontalRoad y={190} height={120} />
          <VerticalRoad x={340} width={120} />
          <circle cx="400" cy="250" r="95" fill={ROAD} stroke={EDGE} strokeWidth="3" />
          <circle cx="400" cy="250" r="42" fill="#8fae7a" stroke={EDGE} strokeWidth="2" />
        </svg>
      );

    case "multi-lane-roundabout":
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
          <HorizontalRoad y={160} height={180} />
          <VerticalRoad x={310} width={180} />
          <circle cx="400" cy="250" r="130" fill={ROAD} stroke={EDGE} strokeWidth="3" />
          <circle cx="400" cy="250" r="90" fill="none" stroke={DASH} strokeWidth="2.5" strokeDasharray="14 10" />
          <circle cx="400" cy="250" r="50" fill="#8fae7a" stroke={EDGE} strokeWidth="2" />
        </svg>
      );

    case "flush-median":
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
          <rect x="0" y="140" width="800" height="220" fill={ROAD} />
          <line x1="0" y1="140" x2="800" y2="140" stroke={EDGE} strokeWidth="3" />
          <line x1="0" y1="360" x2="800" y2="360" stroke={EDGE} strokeWidth="3" />
          {/* Painted flush median with diagonal hatching (turning lane) */}
          <rect x="0" y="220" width="800" height="60" fill={HATCH} stroke={DASH} strokeWidth="2" strokeDasharray="2 0" />
          {Array.from({ length: 20 }).map((_, i) => (
            <line key={i} x1={i * 42} y1="280" x2={i * 42 + 26} y2="220" stroke="#fff" strokeWidth="4" />
          ))}
        </svg>
      );

    case "parallel-parking":
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
          <rect x="0" y="150" width="800" height="200" fill={ROAD} />
          <line x1="0" y1="150" x2="800" y2="150" stroke={EDGE} strokeWidth="3" />
          <line x1="0" y1="350" x2="800" y2="350" stroke={KERB} strokeWidth="5" />
          <line x1="0" y1="250" x2="800" y2="250" stroke={DASH} strokeWidth="3" strokeDasharray="18 14" />
          {[120, 320, 520].map((x) => (
            <rect key={x} x={x} y={280} width={150} height={70} fill="none" stroke="#334155" strokeWidth="2" strokeDasharray="6 6" />
          ))}
        </svg>
      );

    case "lane-change":
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
          <rect x="0" y="80" width="800" height="340" fill={ROAD} />
          <line x1="0" y1="80" x2="800" y2="80" stroke={EDGE} strokeWidth="3" />
          <line x1="0" y1="420" x2="800" y2="420" stroke={EDGE} strokeWidth="3" />
          <line x1="0" y1="250" x2="800" y2="250" stroke="#f2c14e" strokeWidth="3" />
          <line x1="0" y1="165" x2="800" y2="165" stroke={DASH} strokeWidth="3" strokeDasharray="18 14" />
          <line x1="0" y1="335" x2="800" y2="335" stroke={DASH} strokeWidth="3" strokeDasharray="18 14" />
          <path d="M180 380 C 320 380, 320 210, 450 210" fill="none" stroke="#c62828" strokeWidth="4" />
          <Arrowhead x={462} y={209} angleDeg={-6} />
        </svg>
      );

    case "three-point":
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
          <VerticalRoad x={280} width={240} />
          <path d="M330 380 L 545 258" fill="none" stroke="#c62828" strokeWidth="4" />
          <Arrowhead x={560} y={250} angleDeg={-30} />
          <path d="M560 250 L 345 202" fill="none" stroke="#1d4ed8" strokeWidth="4" strokeDasharray="10 8" />
          <Arrowhead x={330} y={200} angleDeg={168} color="#1d4ed8" />
          <path d="M330 200 L 455 126" fill="none" stroke="#c62828" strokeWidth="4" />
          <Arrowhead x={470} y={120} angleDeg={-31} />
        </svg>
      );

    case "u-turn":
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
          <VerticalRoad x={280} width={240} />
          <path
            d="M360 420 L360 220 C360 160, 440 160, 440 220 L440 405"
            fill="none"
            stroke="#c62828"
            strokeWidth="5"
          />
          <Arrowhead x={440} y={420} angleDeg={90} />
        </svg>
      );

    case "railway":
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
          <VerticalRoad x={320} width={160} />
          <rect x="0" y="220" width="800" height="60" fill="#e5e7eb" />
          <line x1="0" y1="235" x2="800" y2="235" stroke="#334155" strokeWidth="4" />
          <line x1="0" y1="265" x2="800" y2="265" stroke="#334155" strokeWidth="4" />
          {Array.from({ length: 22 }).map((_, i) => (
            <line key={i} x1={i * 38} y1="222" x2={i * 38} y2="278" stroke="#334155" strokeWidth="5" />
          ))}
          {/* Level-crossing warning cross (schematic, not an exact regulatory sign copy) */}
          <g transform="translate(400,140)">
            <line x1="-34" y1="-8" x2="34" y2="8" stroke="#c62828" strokeWidth="6" />
            <line x1="-34" y1="8" x2="34" y2="-8" stroke="#c62828" strokeWidth="6" />
          </g>
        </svg>
      );

    case "open-junction":
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
          <HorizontalRoad y={170} height={160} />
          <VerticalRoad x={320} width={160} />
          {/* Clear sightlines at the corners — nothing obstructing the view into the junction */}
          <path d="M260 130 L320 170" stroke={DASH} strokeWidth="2" strokeDasharray="6 6" />
          <path d="M540 130 L480 170" stroke={DASH} strokeWidth="2" strokeDasharray="6 6" />
          <path d="M260 370 L320 330" stroke={DASH} strokeWidth="2" strokeDasharray="6 6" />
          <path d="M540 370 L480 330" stroke={DASH} strokeWidth="2" strokeDasharray="6 6" />
        </svg>
      );

    case "closed-junction":
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
          <HorizontalRoad y={170} height={160} />
          <VerticalRoad x={320} width={160} />
          {/* Restricted sightlines — buildings/hedges close to the corners */}
          <rect x="180" y="60" width="120" height="90" fill={HATCH} stroke={EDGE} strokeWidth="2" />
          <rect x="500" y="60" width="120" height="90" fill={HATCH} stroke={EDGE} strokeWidth="2" />
          <rect x="180" y="350" width="120" height="90" fill={HATCH} stroke={EDGE} strokeWidth="2" />
          <rect x="500" y="350" width="120" height="90" fill={HATCH} stroke={EDGE} strokeWidth="2" />
        </svg>
      );

    case "divergent-movement":
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
          <rect x="0" y="60" width="800" height="380" fill={ROAD} />
          <line x1="0" y1="60" x2="800" y2="60" stroke={EDGE} strokeWidth="3" />
          <path d="M0 440 C 260 440, 320 300, 400 250" stroke={EDGE} strokeWidth="3" fill="none" />
          <path d="M0 250 C 260 250, 300 220, 400 250" stroke={DASH} strokeWidth="3" strokeDasharray="18 14" fill="none" />
          <path d="M400 250 C 500 280, 560 340, 800 380" stroke={EDGE} strokeWidth="3" fill="none" />
          <path d="M400 250 C 520 220, 620 150, 800 90" stroke={EDGE} strokeWidth="3" fill="none" />
          <Arrowhead x={620} y={155} angleDeg={-25} color="#c62828" />
          <Arrowhead x={620} y={345} angleDeg={18} color="#1d4ed8" />
        </svg>
      );

    case "meeting-point":
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
          {/* Narrow road — single shared carriageway, no centre line */}
          <rect x="220" y="0" width="360" height="500" fill={ROAD} />
          <line x1="220" y1="0" x2="220" y2="500" stroke={EDGE} strokeWidth="3" />
          <line x1="580" y1="0" x2="580" y2="500" stroke={EDGE} strokeWidth="3" />
          {/* Widened meeting point / passing bay */}
          <path d="M580 190 C 660 190, 660 310, 580 310" fill={ROAD} stroke={EDGE} strokeWidth="3" />
        </svg>
      );

    case "blank":
    default:
      return (
        <svg {...common}>
          <rect width="800" height="500" fill="#fff" />
        </svg>
      );
  }
}
